import { useState } from 'react';
import { all } from '@/api/wells-fargo/wells-fargo-transaction';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { useExportCsv } from '@/hooks/use-export-csv';
import { useSearch } from '@/hooks/use-search';
import { keepPreviousData, Updater, useQuery } from '@tanstack/react-query';
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import {
    FileSpreadsheet,
    LoaderCircle,
} from 'lucide-react';

import { columns } from './columns';

interface WellsFargoTransactionsTableProps {
    fileIngestionId: number;
}

export function WellsFargoTransactionsTable({
    fileIngestionId,
}: WellsFargoTransactionsTableProps) {
    const { searchTerm, setSearchTerm, pagination, setPagination } = useSearch();
    const [sorting, setSorting] = useState<SortingState>([
        {
            id: 'itemNum',
            desc: true,
        },
    ]);
    const { data, isFetching } = useQuery({
        queryFn: () =>
            all(fileIngestionId,pagination, sorting, {
                searchTerm: searchTerm,
            }),
        queryKey: ['policies-' + fileIngestionId, pagination, sorting, searchTerm],
        placeholderData: keepPreviousData,
        staleTime: 30000,
    });

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});
    const [globalFilter, setGlobalFilter] = useState<any>([]);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const handlePageChange = (
        updater: Updater<PaginationState, PaginationState>,
    ) => {
        setPagination((prev) => {
            const newPagination =
                typeof updater === 'function' ? updater(prev) : updater;

            return newPagination; // Allow page change
        });
    };
    const table = useReactTable({
        data: data?.data ?? [],
        columns: columns,
        rowCount: data?.total ?? 0,
        manualPagination: true,
        manualSorting: true,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: handlePageChange,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            globalFilter,
            pagination,
        },
    });

    const { exportCsv, isExporting } = useExportCsv();

    return (
        <div className="h-1/2 w-full">
            <div className="flex items-center justify-end gap-4 py-4">
                <SearchBar
                    placeholder="Search..."
                    onSearch={setSearchTerm}
                    isLoading={isFetching}
                />
                <div className="flex items-center gap-2">
                    <DropdownMenu
                        open={isDropdownOpen}
                        onOpenChange={(open) => setDropdownOpen(open)}
                    >
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="ml-auto">
                                Columns
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            className="capitalize"
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) => {
                                                column.toggleVisibility(!!value);
                                                console.log(isDropdownOpen);
                                                setDropdownOpen(true);
                                            }}
                                            onSelect={(e) => {
                                                // Stop the dropdown from closing when interacting with the checkbox
                                                e.preventDefault();
                                            }}
                                        >
                                            {column.id}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            {isFetching ? (
                <div className="flex items-center justify-center py-10">
                    <LoaderCircle size={40} className="animate-spin" />
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            <TablePagination table={table} isFetching={isFetching} />
        </div>
    );
}
